/**
 * Pseudo-locale generator (ADR 0017 rule 4): accented characters and ~30% extra length, with
 * brackets so truncation is visible. ICU placeholders, plural/select syntax and inline tags
 * are preserved. Any English text that still appears in the pseudo-locale is hard-coded.
 */
const MAP: Record<string, string> = {
  a: "á", b: "ƀ", c: "ç", d: "ð", e: "é", f: "ƒ", g: "ĝ", h: "ĥ", i: "î", j: "ĵ", k: "ķ", l: "ļ",
  m: "ɱ", n: "ñ", o: "ö", p: "þ", r: "ŕ", s: "š", t: "ţ", u: "û", w: "ŵ", y: "ý", z: "ž",
  A: "Å", B: "Ɓ", C: "Ç", D: "Ð", E: "É", F: "Ƒ", G: "Ĝ", H: "Ĥ", I: "Î", J: "Ĵ", K: "Ķ", L: "Ļ",
  M: "Ṁ", N: "Ñ", O: "Ö", P: "Þ", R: "Ŕ", S: "Š", T: "Ţ", U: "Û", W: "Ŵ", Y: "Ý", Z: "Ž",
};

function accent(text: string): string {
  return [...text].map((ch) => MAP[ch] ?? ch).join("");
}

/** Transform message text, leaving `{...}` ICU arguments and `<tag>` markers untouched,
 *  but accenting the literal text inside plural/select branches. */
export function pseudoMessage(msg: string): string {
  let out = "";
  let depth = 0;
  let i = 0;
  while (i < msg.length) {
    const ch = msg.charAt(i);
    if (ch === "<") {
      const end = msg.indexOf(">", i);
      if (end !== -1) {
        out += msg.slice(i, end + 1);
        i = end + 1;
        continue;
      }
    }
    if (ch === "{") {
      depth++;
      // Copy the argument header (name, type, style) verbatim up to the first nested `{` or `}`.
      let j = i + 1;
      while (j < msg.length && msg[j] !== "{" && msg[j] !== "}") j++;
      out += msg.slice(i, j);
      i = j;
      continue;
    }
    if (ch === "}") {
      depth--;
      out += ch;
      i++;
      // Plural/select keys (e.g. `=0 `, `one `, `other `) between branches stay verbatim.
      let j = i;
      while (j < msg.length && msg[j] !== "{" && msg[j] !== "}" && depth > 0) j++;
      if (depth > 0) {
        out += msg.slice(i, j);
        i = j;
      }
      continue;
    }
    out += accent(ch);
    i++;
  }
  const pad = "~".repeat(Math.max(2, Math.round(msg.length * 0.3)));
  return `[${out} ${pad}]`;
}

export function pseudoLocalize(catalog: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(catalog)) {
    out[k] = typeof v === "string" ? pseudoMessage(v) : pseudoLocalize(v as Record<string, unknown>);
  }
  return out;
}
